import { useEffect, useRef, useState } from "preact/hooks";
import { api, ApiError } from "./api-client";
import { useToast } from "./use-toast";
import { useRegisterDirty } from "./navigation-guard";
import {
  parseValidationErrors,
  type ValidationError,
} from "./parse-validation-errors";
import type { ContentDetailDto, ProjectDto } from "@shared/types";

type Options = {
  projectId: string;
  slug: string;
  // Optional 404 fallback. When provided, a missing document loads with the
  // returned string as the initial draft body and an empty savedBody — so the
  // draft is immediately dirty and the first save creates the row. Without
  // this, 404 surfaces as a loadError (chat editor behavior). Receives the
  // already-fetched project so callers can read the per-project template
  // without a second round trip.
  onMissing?: (
    projectId: string,
    slug: string,
    project: ProjectDto | null,
  ) => Promise<string>;
};

export type DocumentDraft = {
  project: ProjectDto | null;
  savedBody: string;
  draftBody: string;
  setDraftBody: (body: string) => void;
  // True when the draft differs from the saved body — drives "未保存" badge,
  // beforeunload warning, and enables the 保存/破棄 buttons.
  dirty: boolean;
  // Flips to true once the initial load settles (success, 404 fallback, or
  // error). Lets callers swap a Skeleton for the editor instance once.
  loaded: boolean;
  loadError: string | null;
  // Loaded without error. A failed load leaves savedBody/draftBody empty, so
  // editing, AI and save stay off until then — otherwise the first save
  // would overwrite the real document from an empty base.
  ready: boolean;
  // Retries the initial load after a failure.
  reload: () => void;
  saving: boolean;
  saveErrors: ValidationError[] | null;
  clearSaveErrors: () => void;
  // Bumped on each successful save so the revision history panel re-fetches.
  historyRefreshKey: number;
  save: () => Promise<ContentDetailDto | undefined>;
  discardDraft: () => void;
  // Used by the revision restore flow — pushes the restored body into both
  // saved and draft state and fires a success toast.
  applyRestoredDocument: (doc: ContentDetailDto) => void;
};

export function useDocumentDraft({ projectId, slug, onMissing }: Options): DocumentDraft {
  const [project, setProject] = useState<ProjectDto | null>(null);
  const [savedBody, setSavedBody] = useState<string>("");
  const [draftBody, setDraftBody] = useState<string>("");
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveErrors, setSaveErrors] = useState<ValidationError[] | null>(null);
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
  const [reloadKey, setReloadKey] = useState(0);
  const generationRef = useRef(0);
  const toast = useToast();

  const dirty = draftBody !== savedBody;
  const ready = loaded && loadError === null;

  useEffect(() => {
    // Guard against a stale load winning a race: preact-iso reuses this route
    // component across slug changes, so a slow earlier fetch could resolve
    // after a newer one and overwrite the editor with the wrong document.
    let alive = true;
    const generation = generationRef.current + 1;
    generationRef.current = generation;
    setLoaded(false);
    // Same-component navigation (e.g. back/forward between two /pages/:slug
    // URLs) must not leak the previous document's state into this one: a stale
    // savedBody would let 破棄/保存 resurrect the previous article's body under
    // the new slug, and stale error banners would mislabel the new page.
    setLoadError(null);
    setSaveErrors(null);
    setSaving(false);
    setSavedBody("");
    setDraftBody("");
    void (async () => {
      // allSettled so a 404 on the document doesn't drop the project load —
      // we still want the template / name to render the new-doc fallback.
      const [pRes, docRes] = await Promise.allSettled([
        api.getProject(projectId),
        api.getContent(projectId, slug),
      ]);
      if (!alive) return;
      const loadedProject = pRes.status === "fulfilled" ? pRes.value : null;
      // Unconditional: a failed re-fetch after a projectId change must not
      // leave the previous project's data (name / deploy hook) attached.
      setProject(loadedProject);
      try {
        if (docRes.status === "fulfilled") {
          setSavedBody(docRes.value.body);
          setDraftBody(docRes.value.body);
          return;
        }
        const err = docRes.reason;
        if (onMissing && err instanceof ApiError && err.status === 404) {
          try {
            const initial = await onMissing(projectId, slug, loadedProject);
            if (!alive || generationRef.current !== generation) return;
            // savedBody stays "" so dirty=true and the first save creates the row.
            setDraftBody(initial);
            toast.info("新規ドキュメントです。保存するまで作成されません。");
            return;
          } catch (e2) {
            if (!alive || generationRef.current !== generation) return;
            setLoadError((e2 as Error).message);
            return;
          }
        }
        setLoadError((err as Error).message);
      } finally {
        if (alive && generationRef.current === generation) setLoaded(true);
      }
    })();
    return () => {
      alive = false;
    };
    // onMissing is intentionally omitted from deps — callers tend to define it
    // inline, and we only want to re-load on slug/projectId changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, slug, reloadKey]);

  // Links, back/forward, reloads and sign-out are all guarded centrally by
  // NavigationGuardProvider while this reports dirty.
  useRegisterDirty(dirty);

  async function save(): Promise<ContentDetailDto | undefined> {
    if (!ready || !dirty || saving) return undefined;
    const sent = draftBody;
    const generation = generationRef.current;
    setSaving(true);
    setSaveErrors(null);
    try {
      const saved = await api.putContent(projectId, slug, sent);
      if (generationRef.current !== generation) return;
      setSavedBody(saved.body);
      setDraftBody((prev) => (prev === sent ? saved.body : prev));
      toast.success("保存しました");
      setHistoryRefreshKey((k) => k + 1);
      return saved;
    } catch (e) {
      if (generationRef.current !== generation) return;
      setSaveErrors(parseValidationErrors(e));
      return undefined;
    } finally {
      if (generationRef.current === generation) setSaving(false);
    }
  }

  // Reset the draft to the last saved body. No confirmation here — callers are
  // responsible for prompting the user (typically via useConfirm) before
  // invoking this. Keeping the hook free of UI concerns lets the same flow be
  // reused by both the chat and advanced editors with their own dialogs.
  function discardDraft() {
    if (!dirty) return;
    setDraftBody(savedBody);
    setSaveErrors(null);
  }

  function applyRestoredDocument(doc: ContentDetailDto) {
    setSavedBody(doc.body);
    setDraftBody(doc.body);
    toast.success("リビジョンから復元しました");
  }

  return {
    project,
    savedBody,
    draftBody,
    setDraftBody,
    dirty,
    loaded,
    loadError,
    ready,
    reload: () => setReloadKey((k) => k + 1),
    saving,
    saveErrors,
    clearSaveErrors: () => setSaveErrors(null),
    historyRefreshKey,
    save,
    discardDraft,
    applyRestoredDocument,
  };
}
